import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ContactCycleRuleApiService } from '../services/contact-cycle-rule-api.service';
import { ContactCycleRuleRequest } from '../models/contact-cycle-rule.model';
import { ContactCycleApiService } from '../../contact-cycles/services/contact-cycle-api.service';
import { ContactCycleResponse } from '../../contact-cycles/models/contact-cycle.model';
import { ContactAttributeApiService } from '../../contact-attributes/services/contact-attribute-api.service';
import { ContactAttributeResponse } from '../../contact-attributes/models/contact-attribute.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-contact-cycle-rule-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './contact-cycle-rule-form.component.html'
})
export class ContactCycleRuleFormComponent implements OnInit {
  @ViewChild('ruleForm') ruleForm?: NgForm;

  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  ruleId: number | null = null;

  // Selects data
  ciclos: ContactCycleResponse[] = [];
  atributos: ContactAttributeResponse[] = [];
  loadingCiclos = false;
  loadingAtributos = false;

  // Form model
  idContactCycle: number | null = null;
  idContactAttribute: number | null = null;
  operationType: 'SET' | 'INCREMENT' | 'COPY_FROM_FIELD' = 'SET';
  valueExpression = '';
  // ngModel deja el número en null si el usuario vacía el input.
  executionOrder: number | null = 1;
  isActive = true;

  constructor(
    private ruleApi: ContactCycleRuleApiService,
    private cycleApi: ContactCycleApiService,
    private attributeApi: ContactAttributeApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) { }

  ngOnInit(): void {
    this.cargarSelectData();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.ruleId = +id;
      this.cargarRegla(this.ruleId);
    }
  }

  private cargarSelectData(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.loadingCiclos = true;
    this.cycleApi.getCycles(businessId).subscribe({
      next: (data) => {
        this.ciclos = data;
        this.loadingCiclos = false;
      },
      error: () => {
        this.loadingCiclos = false;
      }
    });

    this.loadingAtributos = true;
    this.attributeApi.getAttributes(businessId).subscribe({
      next: (data) => {
        this.atributos = data;
        this.loadingAtributos = false;
      },
      error: () => {
        this.loadingAtributos = false;
      }
    });
  }

  private cargarRegla(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.ruleApi.getRuleById(id, businessId).subscribe({
      next: (regla) => {
        this.idContactCycle = regla.idContactCycle;
        this.idContactAttribute = regla.idContactAttribute;
        this.operationType = regla.operationType;
        this.valueExpression = regla.valueExpression;
        this.executionOrder = regla.executionOrder;
        this.isActive = regla.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la regla';
        this.loading = false;
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Regla' : 'Nueva Regla';
  }

  private get selectedCycleName(): string {
    return this.ciclos.find(c => c.id === this.idContactCycle)?.name ?? String(this.idContactCycle ?? '');
  }

  get valueExpressionHint(): string {
    if (this.operationType === 'SET') return 'Valor fijo a asignar (ej: 3)';
    if (this.operationType === 'INCREMENT') return 'Delta entero a sumar (ej: 10)';
    return 'Nombre del campo fuente (ej: idOrigin)';
  }

  guardar(): void {
    // El botón nunca se deshabilita por validez: si falta algo, el usuario recibe
    // una notificación explícita (antes el submit quedaba mudo y este aviso era inalcanzable).
    const faltantes: string[] = [];
    if (!this.idContactCycle) faltantes.push('Ciclo de Contacto');
    if (!this.idContactAttribute) faltantes.push('Atributo de Contacto');
    if (!this.valueExpression.trim()) faltantes.push('Expresión de Valor');
    if (this.executionOrder === null || this.executionOrder === undefined || this.executionOrder < 1) {
      faltantes.push('Orden de Ejecución');
    }

    if (faltantes.length > 0) {
      this.ruleForm?.form.markAllAsTouched();
      const msg = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.error = msg;
      this.toast.warning({ title: 'Campos incompletos', message: msg });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: ContactCycleRuleRequest = {
      businessId,
      idContactCycle: Number(this.idContactCycle),
      idContactAttribute: Number(this.idContactAttribute),
      operationType: this.operationType,
      valueExpression: this.valueExpression.trim(),
      executionOrder: Number(this.executionOrder),
      isActive: this.isActive
    };

    if (this.isEditing && this.ruleId) {
      this.ruleApi.updateRule(this.ruleId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Regla de ciclo', `Se actualizó la regla del ciclo "${this.selectedCycleName}".`);
          this.router.navigate(['/config-contactos/reglas']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la regla';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.ruleApi.createRule(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Regla de ciclo', `Se creó la regla del ciclo "${this.selectedCycleName}" correctamente.`);
          this.router.navigate(['/config-contactos/reglas']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la regla';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-contactos/reglas']);
  }
}
